/*
 * eu-seller/symbols.js — the marks a seller in the EU may print, as vectors,
 * with the size rules that come with each of them.
 *
 * WHERE EACH DRAWING COMES FROM
 * ─────────────────────────────
 *   ce      The European Commission's own artwork («CE marking — vector and
 *           bitmap images», CE.ai, single-market-economy.ec.europa.eu): the
 *           two letter outlines copied point for point. Proportions are the
 *           graduated drawing of Annex II, Regulation (EC) No 765/2008.
 *   triman  ADEME's pictogram from «Signalétique commune de tri TRIMAN — guide
 *           d'utilisation» (V2, 12/2015), chapter «Sources graphiques»: five
 *           filled paths copied out of the PDF. The guide states its use is
 *           authorised free of charge, for information, within the decree.
 *   bin     The crossed-out wheeled bin of Annex IX of Directive 2012/19/EU
 *           (WEEE) and Annex VI Part B of Regulation (EU) 2023/1542
 *           (batteries) — the law shows a picture, not a construction, so it
 *           is redrawn here to that picture. The bar under it (the date mark of
 *           Art. 15(2) WEEE) follows the usual EN 50419 presentation; the
 *           standard itself is paid and was NOT consulted.
 *   age03   Directive 2009/48/EC Annex V Part B point 1 «0–3» graphic,
 *           redrawn to the picture (black; the red version is Reg. 2025/2509,
 *           from 01.08.2030).
 *   ghs07, ghs09  CLP Annex V pictograms (exclamation mark; environment),
 *           redrawn to the pictures in the consolidated Annex V: black symbol,
 *           white ground, red frame (Annex I 1.2.1.2).
 *   esbin   A plain container for the Spanish «fracción o contenedor»
 *           indication (RD 1055/2022 art. 13.2) — ours, generic, no
 *           Ecoembes/Ecovidrio artwork; the colour is also written as a word.
 *
 * NEVER HERE (licence or trademark): Der Grüne Punkt / Punto Verde, GINETEX
 * care symbols, the EN 15494 candle pictograms, Citeo's Info-tri layout.
 *
 * A symbol is a viewBox and filled layers, so the SVG preview and the PDF
 * draw the same outlines from the same numbers (eu-seller/labels.js).
 * Pure module: no DOM. Tested by tests/js/eu-seller-symbols.test.mjs.
 */
(function (root, factory) {
    'use strict';
    var api = factory();
    if (typeof module !== 'undefined' && module.exports) module.exports = api;
    if (root) root.EuSymbols = api;
})(typeof self !== 'undefined' ? self : (typeof window !== 'undefined' ? window : null), function () {
    'use strict';

    /* ── path helpers (all closed, filled outlines) ──────────────────────── */
    function f(n) { return Math.round(n * 1000) / 1000; }
    function circle(cx, cy, r) {
        // Two arcs: a closed circle any SVG/PDF path parser accepts.
        return 'M' + f(cx - r) + ' ' + f(cy) + 'A' + f(r) + ' ' + f(r) + ' 0 1 0 ' + f(cx + r) + ' ' + f(cy)
            + 'A' + f(r) + ' ' + f(r) + ' 0 1 0 ' + f(cx - r) + ' ' + f(cy) + 'Z';
    }
    function rect(x, y, w, h) {
        return 'M' + f(x) + ' ' + f(y) + 'H' + f(x + w) + 'V' + f(y + h) + 'H' + f(x) + 'Z';
    }
    function poly(pts) {
        return pts.map(function (p, i) { return (i ? 'L' : 'M') + f(p[0]) + ' ' + f(p[1]); }).join('') + 'Z';
    }
    /* A straight stroke of width w from a to b, as a filled quadrilateral. */
    function bar(ax, ay, bx, by, w) {
        var dx = bx - ax, dy = by - ay, L = Math.sqrt(dx * dx + dy * dy) || 1;
        var nx = -dy / L * w / 2, ny = dx / L * w / 2;
        return poly([[ax + nx, ay + ny], [bx + nx, by + ny], [bx - nx, by - ny], [ax - nx, ay - ny]]);
    }

    /* ── CE (Commission artwork, CE.ai «lettre» layer, y flipped) ────────── */
    var CE_D = 'M840 598.52L840 507.87C830.2 509.28 820.19 510 810 510C709.46 510 625.43 439.36 604.83 345L780 345L780 255L604.83 255C625.43 160.64 709.46 90 810 90C820.19 90 830.2 90.72 840 92.12L840 1.48C830.13 0.5 820.13 0 810 0C644.31 0 510 134.31 510 300C510 465.69 644.31 600 810 600C820.13 600 830.13 599.5 840 598.52Z'
        + 'M0 300C0 465.69 134.31 600 300 600C310.81 600 321.49 599.43 332 598.32L332 507.58C321.57 509.18 310.88 510 300 510C184.02 510 90 415.98 90 300C90 184.02 184.02 90 300 90C310.88 90 321.57 90.82 332 92.42L332 1.68C321.49 0.57 310.81 0 300 0C134.31 0 0 134.31 0 300Z';

    /* ── Triman (ADEME guide 2015, «Sources graphiques», page 45) ────────── */
    var TRIMAN_D = 'M27.63 23.272C25.576 26.159 27.321 29.954 29.445 31.878C31.378 33.629 32.932 34.044 33.135 34.092L34.12 32.289L36.972 39.718L28.917 41.28L29.987 39.388L28.898 38.804C25.506 36.699 24.789 33.835 24.555 32.941C24.151 31.403 23.549 26.8 27.63 23.272Z'
        + 'M27.593 23.396C29.058 26.593 33.354 27.211 36.194 26.524C38.711 25.915 39.908 24.925 40.109 24.745L40.099 24.726L39.022 22.948L47.125 24.637L44.19 31.958L43.106 30.114L42.046 30.661C38.368 32.274 35.444 31.324 34.528 31.036C32.948 30.54 28.544 28.569 27.593 23.396Z'
        + 'M41.92 15.678C41.359 19.501 38.943 21.335 38.193 21.915C36.901 22.915 32.785 25.385 27.705 23.356C31.319 23.908 34.183 20.794 35.111 18.143C35.943 15.766 35.698 14.262 35.652 14.029L35.625 14.027L33.473 13.938L39.325 8.33L44.218 14.63L42.012 14.508L41.92 15.678Z'
        + 'M17.341 12.454C15.493 12.454 13.994 11.027 13.994 9.264C13.994 7.5 15.493 6.072 17.341 6.072C19.19 6.072 20.688 7.5 20.688 9.264C20.688 11.027 19.19 12.454 17.341 12.454Z'
        + 'M21.917 0C9.832 0 0 9.376 0 20.902C0 32.425 9.832 41.802 21.917 41.802C22.564 41.802 23.2 41.763 23.831 41.709L23.978 43.808L27.986 40.11L23.499 36.846L23.669 39.05C23.091 39.101 22.509 39.138 21.917 39.138C20.988 39.138 20.076 39.07 19.182 38.947L19.092 38.392L17.012 21.47C16.726 18.276 18.639 17.767 19.82 18.245L26.465 20.791C27.229 21.104 27.593 20.496 27.593 20.496C27.593 20.496 28.024 19.86 27.256 19.391C27.256 19.391 18.196 13.667 16.265 13.322C13.632 12.852 11.025 14.887 10.443 17.538L7.23 32.565C4.462 29.4 2.794 25.334 2.794 20.902C2.794 10.846 11.372 2.665 21.917 2.665C27.549 2.665 32.619 5 36.121 8.707L38.086 6.808C34.076 2.627 28.312 0 21.917 0Z'
        + 'M12.159 27.967C12.445 27.22 12.873 27.267 12.873 27.267C12.873 27.267 13.257 27.188 13.47 27.942L16.599 38.414C13.99 37.692 11.609 36.456 9.587 34.824L12.159 27.967Z';

    /* ── the crossed-out wheeled bin, drawn to Annex IX ───────────────────── */
    /* Every part is its own layer: overlapping outlines of opposite winding
     * in one non-zero path cancel out (the lid ends came out white). */
    function binLayers(withBar) {
        var ink = function (d) { return { d: d, fill: 'ink' }; };
        var layers = [
            // Body: the trapezoid as a ring (outer minus inner).
            { d: poly([[26, 23.6], [74, 23.6], [67.6, 83], [32.4, 83]]) + poly([[29.6, 26.4], [70.4, 26.4], [64.6, 80.2], [35.4, 80.2]]), fill: 'ink', rule: 'evenodd' },
            // Lid: a bar across the top with a raised handle in the middle.
            ink(rect(21, 19, 58, 3.6)),
            ink(poly([[33, 19], [35, 12.6], [65, 12.6], [67, 19], [63.4, 19], [62, 15.8], [38, 15.8], [36.6, 19]])),
            // Hinge housing on the right of the rim, and the handle on the body.
            ink(rect(74, 22.6, 3.4, 8.4)),
            ink(rect(43, 37, 14, 2.8)),
            // The cross: corner to corner, over everything.
            ink(bar(6, 4, 94, 96, 3.6)),
            ink(bar(94, 4, 6, 96, 3.6)),
            // The wheel sits over the corner: cleared, then drawn.
            { d: circle(65.5, 82.6, 7.4), fill: 'paper' },
            { d: circle(65.5, 82.6, 7.4) + circle(65.5, 82.6, 4.9), fill: 'ink', rule: 'evenodd' },
            ink(circle(65.5, 82.6, 2.1)),
        ];
        if (withBar) layers.push(ink(rect(20, 104, 60, 7)));
        return layers;
    }

    /* ── «0–3» (2009/48 Annex V B 1) ────────────────────────────────────── */
    function age03Layers() {
        var stroke = [];
        // Face, top right: outline ring, eyes, a downturned mouth, a curl of hair.
        stroke.push(circle(64, 37, 12.5) + circle(64, 37, 10.2));
        stroke.push(circle(59.8, 35, 1.5) + circle(68.2, 35, 1.5));
        stroke.push('M57.6 46.2C60.6 42.4 67.4 42.4 70.4 46.2L68.6 47.4C66.2 44.8 61.8 44.8 59.4 47.4Z');
        stroke.push('M60.8 25.2C58.2 22.6 59.4 18.6 63.2 18.8C66.4 19 67.2 22.4 65 24.4L63.4 23.2C64.4 22.2 64 20.8 62.8 20.8C61.2 20.8 60.8 22.8 62.4 24Z');
        // «0–3», bottom left, drawn as outlines so no font is involved.
        var zero = 'M19.8 70C19.8 64.6 22.2 61.4 25.4 61.4C28.6 61.4 31 64.6 31 70C31 75.4 28.6 78.6 25.4 78.6C22.2 78.6 19.8 75.4 19.8 70Z'
            + 'M22.4 70C22.4 74 23.6 76.2 25.4 76.2C27.2 76.2 28.4 74 28.4 70C28.4 66 27.2 63.8 25.4 63.8C23.6 63.8 22.4 66 22.4 70Z';
        var dash = rect(33.4, 69.2, 5.2, 2.4);
        var three = 'M40.6 63.4C41.8 62 43.6 61.4 45.4 61.4C48.6 61.4 50.6 63.2 50.6 65.6C50.6 67.4 49.6 68.8 47.8 69.6C50 70.2 51.2 71.8 51.2 73.8C51.2 76.8 48.8 78.6 45.4 78.6C43.2 78.6 41.2 77.8 40 76.4L41.6 74.8C42.6 75.8 43.9 76.3 45.4 76.3C47.4 76.3 48.6 75.3 48.6 73.8C48.6 72 47.2 70.9 44.6 70.9H43.6V68.7H44.6C46.8 68.7 48 67.6 48 65.8C48 64.5 46.9 63.7 45.3 63.7C44 63.7 42.9 64.2 42.1 65Z';
        return [
            { d: circle(50, 50, 48.5) + circle(50, 50, 42.5), fill: 'ink', rule: 'evenodd' },
            { d: stroke.slice(0, 4).join(''), fill: 'ink', rule: 'evenodd' },
            { d: zero, fill: 'ink', rule: 'evenodd' },
            { d: dash + three, fill: 'ink' },
            { d: bar(19.9, 19.9, 80.1, 80.1, 6.4), fill: 'ink' },
        ];
    }

    /* ── CLP pictograms: red frame, white ground, black symbol ───────────── */
    function diamondLayers(symbolLayers) {
        var outer = poly([[50, 0.8], [99.2, 50], [50, 99.2], [0.8, 50]]);
        var inner = poly([[50, 8.6], [91.4, 50], [50, 91.4], [8.6, 50]]);
        return [{ d: outer, fill: 'paper' }, { d: outer + inner, fill: '#e3001b', rule: 'evenodd', fixed: true }].concat(symbolLayers);
    }
    function ghs07Layers() {
        var stem = 'M45.2 29.6C45.2 26.9 47.3 25.4 50 25.4C52.7 25.4 54.8 26.9 54.8 29.6L52.6 61.6H47.4Z';
        return diamondLayers([{ d: stem + circle(50, 69.6, 4.4), fill: 'ink' }]);
    }
    function ghs09Layers() {
        var ink = function (d) { return { d: d, fill: 'ink' }; };
        return diamondLayers([
            // Ground and water at the foot of the tree.
            ink('M23.6 74.6C32 71.2 43.6 70.2 52 71C60.4 71.8 68.8 72.6 77.2 71.6L73.8 76.4H27.2Z'),
            // A bare, dead tree: tapering trunk and five branches.
            ink(poly([[31.6, 72.4], [34.4, 40], [37.8, 40], [38.6, 72.4]])),
            ink(bar(35.6, 54, 25.2, 44.6, 2.8)),
            ink(bar(25.8, 45.2, 24.6, 39.6, 2)),
            ink(bar(36, 46, 29.6, 31.4, 2.8)),
            ink(bar(36.6, 50.6, 45.2, 36.4, 2.8)),
            ink(bar(44.2, 38.4, 48.6, 36.2, 2)),
            ink(bar(36.4, 41.6, 40.6, 28.6, 2.4)),
            // A dead fish, belly up, head to the right.
            ink('M46.6 63.8C50.8 58 58.8 55.2 66.8 56.4C69.8 56.8 72.2 58.4 73.4 60.2C71.2 63.4 66.2 65.8 60.6 66C55.8 66.2 51 65.4 46.6 63.8Z'),
            ink('M47.6 63.6L41.6 58.6L42.8 67.4Z'),
            { d: circle(68.4, 59.6, 1.4), fill: 'paper' },
        ]);
    }

    /* ── cosmetics, Reg. 1223/2009 Annex VII (redrawn to the pictures) ───── */
    function paoLayers() {
        // Open jar: the body with its rim, and the lid lifted and tilted above it.
        var body = 'M18 60C18 54 34 50 50 50C66 50 82 54 82 60V86C82 92 66 96 50 96C34 96 18 92 18 86Z';
        var rim = 'M18 60C18 66 34 70 50 70C66 70 82 66 82 60';
        var lid = poly([[14.6, 42.8], [77.4, 16.4], [81.6, 25.6], [18.8, 52]]);
        return [
            { d: body, fill: 'paper' },
            { d: body + rim, fill: 'none', stroke: 'ink', sw: 4.2 },
            { d: lid, fill: 'paper' },
            { d: lid, fill: 'none', stroke: 'ink', sw: 4.2 },
        ];
    }
    function hourglassLayers() {
        var glass = 'M28 12H72C72 31 54 42 54 50C54 58 72 69 72 88H28C28 69 46 58 46 50C46 42 28 31 28 12Z';
        var sand = 'M30.6 70H69.4C70.9 75.6 72 81.6 72 88H28C28 81.6 29.1 75.6 30.6 70Z';
        return [
            { d: glass, fill: 'paper' },
            { d: sand, fill: 'ink' },
            { d: glass, fill: 'none', stroke: 'ink', sw: 4 },
            { d: rect(20, 4, 60, 8) + rect(20, 88, 60, 8), fill: 'ink' },
        ];
    }

    /* ── «for food contact», Reg. 1935/2004 Annex II (traced from the 2964 px
     * picture of the consolidated act, in that picture's displayed units) ── */
    function fcmLayers() {
        var glass = 'M10 8H1352V800C1340 1000 1150 1200 828 1262L826 1548C860 1700 1050 1800 1290 1808L1290 1905'
            + 'C1000 1905 720 1780 706 1560L716 1160C1000 1160 1180 1060 1226 836L1228 102H133V845'
            + 'C180 1030 330 1160 616 1160L616 1565C590 1700 400 1915 40 1905L38 1812C300 1820 440 1700 494 1548'
            + 'L505 1272C200 1220 10 1040 10 800Z';
        var fork = rect(1420, 8, 132, 497) + rect(1605, 8, 127, 497)
            + 'M1793 8H1903V600C1903 720 1830 800 1733 855V1905H1577V768C1700 770 1790 700 1792 600Z';
        return [{ d: glass, fill: 'ink' }, { d: fork, fill: 'ink' }];
    }

    /* ── USB-C charger pictogram and label, Dir. 2014/53 Annex Ia Parts III–IV
     * (as amended by Dir. 2022/2380): built from the dimensioned drawing in
     * units of a = 60 — frame 4a/3 × 11a/6, charger body a × a, head 2a/3 ×
     * a/4 centred, two pins a/12 wide with a/4 between, a/6 margins. «a» is
     * at least 7 mm (Part III point 2, Part IV point 2). */
    var A = 60;
    /* `open`: the label variant leaves the body unfilled — its text («XX–YY W»)
     * is drawn by the label UNDER the symbol layer and must show through. */
    function chargerLayers(ox, oy, sw, open) {
        var bx = ox, by = oy + A / 2;
        var hx = bx + A / 6, hy = oy + A / 4;
        var pinW = A / 12, gap = A / 4, px = hx + (2 * A / 3 - (2 * pinW + gap)) / 2;
        var r = 4;
        var body = 'M' + f(bx + r) + ' ' + f(by) + 'H' + f(bx + A - r) + 'Q' + f(bx + A) + ' ' + f(by) + ' ' + f(bx + A) + ' ' + f(by + r)
            + 'V' + f(by + A - r) + 'Q' + f(bx + A) + ' ' + f(by + A) + ' ' + f(bx + A - r) + ' ' + f(by + A)
            + 'H' + f(bx + r) + 'Q' + f(bx) + ' ' + f(by + A) + ' ' + f(bx) + ' ' + f(by + A - r)
            + 'V' + f(by + r) + 'Q' + f(bx) + ' ' + f(by) + ' ' + f(bx + r) + ' ' + f(by) + 'Z';
        return [
            { d: rect(px, oy + sw / 2, pinW, A / 4 - sw / 2) + rect(px + pinW + gap, oy + sw / 2, pinW, A / 4 - sw / 2), fill: 'ink' },
            { d: rect(hx, hy, 2 * A / 3, A / 4), fill: 'paper', stroke: 'ink', sw: sw },
            { d: body, fill: open ? 'none' : 'paper', stroke: 'ink', sw: sw },
        ];
    }
    function usbcLayers(crossed) {
        var W = 4 * A / 3, H = 11 * A / 6, sw = 2;
        var L = [{ d: rect(sw / 2, sw / 2, W - sw, H - sw), fill: 'paper', stroke: 'ink', sw: sw }]
            .concat(chargerLayers(A / 6, A / 6, sw));
        if (crossed) L.push({ d: bar(sw, sw, W - sw, H - sw, 3), fill: 'ink' });
        return L;
    }

    /* ── Spanish container (generic): lid + tapered body, coloured ───────── */
    function esbinLayers(colour) {
        var lid = 'M8 14C8 10 11 8 15 8H45C49 8 52 10 52 14V17H8Z';
        var body = poly([[10, 19], [50, 19], [46.8, 66], [13.2, 66]]);
        var slot = rect(22, 11.2, 16, 2.4);
        return [
            { d: lid + body, fill: colour || 'paper' },
            { d: lid + body, fill: 'none', stroke: 'ink', sw: 2.2 },
            { d: slot, fill: 'ink' },
        ];
    }

    /* ── the registry ───────────────────────────────────────────────────── */
    var SYMBOLS = {
        ce: {
            vb: [840, 600], layers: function () { return [{ d: CE_D, fill: 'ink' }]; },
            // Annex II.3 765/2008: «at least 5 mm high» where specific legislation sets none.
            minH: 5, rule: 'ce_min',
            src: 'European Commission, CE marking vector images (CE.ai); Reg. (EC) 765/2008 Annex II',
        },
        triman: {
            vb: [47.125, 43.808], layers: function () { return [{ d: TRIMAN_D, fill: 'ink' }]; },
            // ADEME charter: 6 mm compact version, 10 mm standard — recommendations.
            minH: 6, prefH: 10, rule: 'triman_min',
            src: 'ADEME, Signalétique commune de tri TRIMAN — guide d’utilisation (V2, 12/2015), Sources graphiques',
        },
        weee: {
            vb: [100, 100], layers: function () { return binLayers(false); },
            minH: null, rule: 'weee_size',
            src: 'Directive 2012/19/EU Annex IX (redrawn to the published picture)',
        },
        weeeBar: {
            vb: [100, 112], layers: function () { return binLayers(true); },
            minH: null, rule: 'weee_size',
            src: 'Directive 2012/19/EU Annex IX + Art. 15(2) date mark (bar as commonly applied under EN 50419; the standard was not consulted)',
        },
        battery: {
            vb: [100, 100], layers: function () { return binLayers(false); },
            minH: null, rule: 'battery_area',
            src: 'Regulation (EU) 2023/1542 Art. 13(4) and Annex VI Part B (redrawn to the published picture)',
        },
        age03: {
            vb: [100, 100], layers: age03Layers,
            minH: null, rule: 'age03_size',
            src: 'Directive 2009/48/EC Annex V Part B point 1 (redrawn to the published picture)',
        },
        ghs07: {
            vb: [100, 100], layers: ghs07Layers,
            minH: 10, rule: 'ghs_min',
            src: 'Regulation (EC) 1272/2008 Annex V (GHS07), Annex I 1.2.1',
        },
        ghs09: {
            vb: [100, 100], layers: ghs09Layers,
            minH: 10, rule: 'ghs_min',
            src: 'Regulation (EC) 1272/2008 Annex V (GHS09), Annex I 1.2.1',
        },
        pao: {
            vb: [100, 100], layers: paoLayers,
            minH: null, rule: null,
            src: 'Regulation (EC) 1223/2009 Annex VII point 2 (redrawn to the published picture)',
        },
        hourglass: {
            vb: [100, 100], layers: hourglassLayers,
            minH: null, rule: null,
            src: 'Regulation (EC) 1223/2009 Annex VII point 3 (redrawn to the published picture)',
        },
        fcm: {
            vb: [1913, 1932], layers: fcmLayers,
            minH: null, rule: null,
            src: 'Regulation (EC) 1935/2004 Annex II (traced from the published picture)',
        },
        usbcIn: {
            vb: [4 * A / 3, 11 * A / 6], layers: function () { return usbcLayers(false); },
            minH: 7 * 11 / 6, rule: 'usbc_a',
            src: 'Directive 2014/53/EU Annex Ia Part III point 1.1 (Dir. 2022/2380), built from the dimensioned drawing',
        },
        usbcOut: {
            vb: [4 * A / 3, 11 * A / 6], layers: function () { return usbcLayers(true); },
            minH: 7 * 11 / 6, rule: 'usbc_a',
            src: 'Directive 2014/53/EU Annex Ia Part III point 1.2 (Dir. 2022/2380), built from the dimensioned drawing',
        },
        usbcLabel: {
            vb: [A, 3 * A / 2], layers: function () { return chargerLayers(0, 0, 2, true); },
            minH: 7 * 3 / 2, rule: 'usbc_a',
            // The text «XX–YY W» / «USB PD» is laid out by the label (EuCompose), inside the body square.
            body: { x: 0, y: A / 2, w: A, h: A },
            src: 'Directive 2014/53/EU Annex Ia Part IV (Dir. 2022/2380), built from the dimensioned drawing',
        },
        esbin: {
            vb: [60, 70], layers: function (o) { return esbinLayers(o && o.colour); },
            minH: null, rule: null,
            src: 'Generic container drawn for RD 1055/2022 art. 13.2 (fraction or container)',
        },
    };

    /* Spanish household fractions: the container colour, and the word for it
     * (the RD asks for «la fracción o contenedor»; a colour word survives
     * black-and-white printing). */
    var ES_CONTAINERS = {
        amarillo: { colour: '#ffd400', word: 'Contenedor amarillo' },
        azul: { colour: '#1f6fd1', word: 'Contenedor azul' },
        verde: { colour: '#1e9e4a', word: 'Contenedor verde' },
        gris: { colour: '#8a8a8a', word: 'Contenedor gris (resto)' },
    };

    function resolve(fill, opts) {
        if (fill === 'ink') return (opts && opts.ink) || '#000000';
        if (fill === 'paper') return (opts && opts.paper) || '#ffffff';
        return fill;
    }

    /* Layers with colours resolved: [{d, fill, stroke, sw, rule}] in viewBox units. */
    function layers(id, opts) {
        var s = SYMBOLS[id];
        if (!s) return [];
        return s.layers(opts || {}).map(function (L) {
            return {
                d: L.d, rule: L.rule || 'nonzero',
                fill: L.fill === 'none' ? null : resolve(L.fill, opts),
                stroke: L.stroke ? resolve(L.stroke, opts) : null,
                sw: L.sw || 0,
            };
        });
    }

    /* Contain-fit into a box (mm): scale and offsets, centred. */
    function fit(id, box) {
        var s = SYMBOLS[id];
        var vw = s.vb[0], vh = s.vb[1];
        var k = Math.min(box.w / vw, box.h / vh);
        var w = vw * k, h = vh * k;
        return { k: k, w: w, h: h, x: box.x + (box.w - w) / 2, y: box.y + (box.h - h) / 2 };
    }

    /* A standalone SVG (download / preview). Units: mm when wMm given. */
    function svg(id, opts) {
        opts = opts || {};
        var s = SYMBOLS[id];
        if (!s) return '';
        var vw = s.vb[0], vh = s.vb[1];
        var wMm = opts.wMm || null, hMm = wMm ? wMm * vh / vw : null;
        var head = '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + vw + ' ' + vh + '"'
            + (wMm ? ' width="' + f(wMm) + 'mm" height="' + f(hMm) + 'mm"' : '') + '>';
        var body = layers(id, opts).map(function (L) {
            return '<path d="' + L.d + '" fill="' + (L.fill || 'none') + '"'
                + (L.rule === 'evenodd' ? ' fill-rule="evenodd"' : '')
                + (L.stroke ? ' stroke="' + L.stroke + '" stroke-width="' + L.sw + '" stroke-linejoin="round"' : '') + '/>';
        }).join('');
        return head + (opts.title ? '<title>' + String(opts.title).replace(/[<&]/g, '') + '</title>' : '') + body + '</svg>';
    }

    /* ── size rules ─────────────────────────────────────────────────────── */

    /* Battery separate-collection symbol, Reg. 2023/1542 Art. 13(4):
     *   ≥ 3 % of the area of the largest side, at most 5 × 5 cm;
     *   cylindrical cells: ≥ 1.5 % of the surface area, at most 5 × 5 cm;
     *   if that would be smaller than 0.47 × 0.47 cm — not on the battery,
     *   but ≥ 1 × 1 cm on the packaging.
     * Dimensions in mm. The symbol is taken as square (the drawing is). */
    function batteryRule(b) {
        b = b || {};
        var area;
        if (b.shape === 'cylinder') {
            var r = (+b.d || 0) / 2, h = +b.h || 0;
            area = 2 * Math.PI * r * h + 2 * Math.PI * r * r;
            area *= 0.015;
        } else {
            var dims = [+b.w || 0, +b.h || 0, +b.t || 0].sort(function (a, c) { return c - a; });
            area = dims[0] * dims[1] * 0.03;
        }
        var side = Math.sqrt(Math.max(0, area));
        if (!(side > 0)) return { ok: false, reason: 'dims' };
        var capped = Math.min(side, 50);
        if (side < 4.7) return { onBattery: false, packagingMin: 10, side: side };
        return { onBattery: true, minSide: capped, maxSide: 50, side: side, cdpbMinArea: capped * capped / 4 };
    }

    /* x-height ↔ type size for the face the labels embed (Noto Sans:
     * OS/2 sxHeight 536/1000 regular, 546 bold — read from the TTF the PDF
     * embeds). Warnings of Reg. (EU) 2025/2509 (toys, from 01.08.2030) need an
     * x-height ≥ 1.2 mm, or 0.9 mm when the largest surface of the packaging
     * is under 80 cm². Applied here to warnings as the coming rule and as good
     * practice now. */
    var XH = { regular: 0.536, bold: 0.546 };
    function xHeight(sizeMm, bold) { return sizeMm * (bold ? XH.bold : XH.regular); }
    function minSizeForXHeight(xMm, bold) { return xMm / (bold ? XH.bold : XH.regular); }
    function xHeightRule(largestCm2) {
        return (largestCm2 != null && +largestCm2 > 0 && +largestCm2 < 80) ? 0.9 : 1.2;
    }

    /* CLP Annex I 1.2.1.3–4: each pictogram ≥ 1 cm² and ≥ 1/15 of the label
     * area for Art. 17 information; Table 1.3 by package capacity. */
    var CLP_TABLE = [
        { upToL: 3, label: [52, 74], labelIfPossible: true, picto: 10, pictoIfPossible: 16 },
        { upToL: 50, label: [74, 105], picto: 23 },
        { upToL: 500, label: [105, 148], picto: 32 },
        { upToL: Infinity, label: [148, 210], picto: 46 },
    ];
    /* The pictogram is a square set on a point; Table 1.3 gives its sides
     * («not smaller than 10 × 10»), so the 1 cm² floor and the 1/15 of the
     * label are both read on the square itself. Its upright bounding box —
     * what a layout measures — is √2 times the side. */
    function clpRule(capacityL, labelWmm, labelHmm) {
        var row = CLP_TABLE[0];
        for (var i = 0; i < CLP_TABLE.length; i++) { if ((+capacityL || 0) <= CLP_TABLE[i].upToL) { row = CLP_TABLE[i]; break; } }
        var fifteenth = (labelWmm > 0 && labelHmm > 0) ? Math.sqrt(labelWmm * labelHmm / 15) : 0;
        var side = Math.max(row.picto, fifteenth);
        return {
            row: row,
            minSide: side,
            minBox: side * Math.SQRT2,
            prefSide: row.pictoIfPossible || null,
            labelMin: row.label, labelIfPossible: !!row.labelIfPossible,
        };
    }

    /* One verdict per symbol drawn at hMm (its box height in mm). */
    function check(id, hMm, ctx) {
        var s = SYMBOLS[id];
        ctx = ctx || {};
        if (!s) return null;
        var out = { id: id, h: hMm, ok: true, code: null, need: null };
        if (id === 'ce' && hMm < 5 - 1e-6) { out.ok = false; out.code = 'ce_small'; out.need = 5; }
        if (id === 'triman') {
            if (hMm < 6 - 1e-6) { out.ok = false; out.code = 'triman_small'; out.need = 6; }
            else if (hMm < 10 - 1e-6) { out.code = 'triman_compact'; out.need = 10; }
        }
        if (id === 'battery') {
            var r = batteryRule(ctx.battery);
            if (r.reason === 'dims') { out.code = 'battery_dims'; }
            else if (!r.onBattery) { out.code = 'battery_pack'; out.need = r.packagingMin; out.ok = ctx.onPackaging ? hMm >= r.packagingMin - 1e-6 : true; }
            else if (hMm < r.minSide - 1e-6) { out.ok = false; out.code = 'battery_small'; out.need = Math.ceil(r.minSide * 10) / 10; }
            else if (hMm > 50 + 1e-6) { out.ok = false; out.code = 'battery_big'; out.need = 50; }
        }
        if (s.rule === 'usbc_a') {
            // «a» is the body side: the pictogram is 11a/6 tall, the label 3a/2.
            var a = hMm / (id === 'usbcLabel' ? 1.5 : 11 / 6);
            if (a < 7 - 1e-6) { out.ok = false; out.code = 'usbc_small'; out.need = Math.ceil(7 * (id === 'usbcLabel' ? 1.5 : 11 / 6) * 10) / 10; }
        }
        if ((id === 'ghs07' || id === 'ghs09')) {
            // hMm is the drawn box; the frame spans 0.8–99.2 % of it.
            var c = clpRule(ctx.capacityL, ctx.labelW, ctx.labelH);
            var drawnSide = hMm * 0.984 / Math.SQRT2;
            if (drawnSide < c.minSide - 1e-6) { out.ok = false; out.code = 'ghs_small'; out.need = Math.ceil(c.minSide * Math.SQRT2 / 0.984 * 10) / 10; }
        }
        return out;
    }

    return {
        SYMBOLS: SYMBOLS,
        ES_CONTAINERS: ES_CONTAINERS,
        layers: layers,
        fit: fit,
        svg: svg,
        check: check,
        batteryRule: batteryRule,
        clpRule: clpRule,
        xHeight: xHeight,
        minSizeForXHeight: minSizeForXHeight,
        xHeightRule: xHeightRule,
        XH: XH,
        __paths: { circle: circle, rect: rect, poly: poly, bar: bar },
    };
});
